import APIService from '@/services/APIService';

function normalizeCollection(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.results)) return payload.data.results;
    return [];
}

function resolveId(userLike) {
    if (!userLike) return '';
    return String(userLike.id || userLike._id || userLike.sub || '').trim();
}

function normalizeUser(userLike) {
    if (!userLike || typeof userLike !== 'object') return null;
    const id = resolveId(userLike);
    if (!id) return null;
    return { ...userLike, id };
}

function hasCourseMatch(coursesPayload, targetCourseId) {
    const courses = normalizeCollection(coursesPayload);
    return courses.some((course) => {
        if (!course) return false;
        const directId = String(course.id || course._id || '');
        const nestedCourse = course.course;
        const nestedId = typeof nestedCourse === 'object'
            ? String(nestedCourse?.id || nestedCourse?._id || '')
            : String(nestedCourse || '');
        return directId === targetCourseId || nestedId === targetCourseId;
    });
}

export async function getMesskursVisibleUsers(managerUserId, courseId) {
    const normalizedManagerId = String(managerUserId || '').trim();
    const normalizedCourseId = String(courseId || '').trim();
    if (!normalizedManagerId || !normalizedCourseId) return [];

    const managedUsersResponse = await APIService.getManagedUsers(normalizedManagerId).catch((error) => {
        console.warn('Error loading managed users for manager', normalizedManagerId, error);
        return [];
    });
    const managedUsers = normalizeCollection(managedUsersResponse);

    const managerIds = [...new Set(
        [normalizedManagerId, ...managedUsers.map((user) => resolveId(user))]
            .map((id) => String(id || '').trim())
            .filter(Boolean)
    )];

    const teamMemberLists = await Promise.all(managerIds.map(async (id) => {
        const members = await APIService.getTeamMembers(id).catch((error) => {
            console.warn('Error loading team members for manager', id, error);
            return [];
        });
        return normalizeCollection(members);
    }));

    const teamMembersById = new Map();
    for (const members of teamMemberLists) {
        for (const rawMember of members) {
            const member = normalizeUser(rawMember);
            if (!member) continue;
            if (!teamMembersById.has(member.id)) {
                teamMembersById.set(member.id, member);
            }
        }
    }

    const allMembers = [...teamMembersById.values()];

    const visibilityChecks = await Promise.all(allMembers.map(async (member) => {
        const memberId = resolveId(member);
        if (!memberId) return null;

        const coursesPayload = await APIService.getUserCourses(memberId).catch((error) => {
            console.warn('Error loading courses for team member', memberId, error);
            return [];
        });
        return hasCourseMatch(coursesPayload, normalizedCourseId) ? member : null;
    }));

    return visibilityChecks.filter(Boolean);
}
